<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ device ? device.pageTitle : '' }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditVideo.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToVideo">{{ $t('componentEditVideo.title') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ device ? device.pageTitle : '' }}</li>
                    </ol>
                </div>
            </div>

            <!-- Formulaire : validé à l'enregistrement pour pouvoir ouvrir les paramètres avancés -->
            <form v-if="device" ref="formRef" id="videoDeviceForm" name="videoDeviceForm" novalidate @submit.prevent="handleSubmit" class="pb-4">
                <div class="row">
                    <div class="col-xxl-8 col-xl-10">
                        <div class="card mb-3">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('common.generalConfig') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-2 col-12">
                                        <label for="video-name" class="form-label">{{ $t('componentEditVideoDevice.label') }}</label>
                                        <input id="video-name" type="text" class="form-control" v-model="form.name" maxlength="32"
                                            :placeholder="$t('componentEditVideoDevice.labelPlaceholder')" autocomplete="off" required />
                                    </div>

                                    <template v-if="hasOwnEndpoint">
                                        <!-- Détection ONVIF (WS-Discovery lancée par le LINK) -->
                                        <div v-if="device.isOnvif" class="mb-2 col-12">
                                            <button id="video-discover" type="button" class="btn btn-outline-primary" @click="discoverOnvifDevices">
                                                <i class="mdi mdi-magnify me-1"></i>{{ $t('componentEditVideoDevice.discover') }}
                                            </button>
                                        </div>

                                        <div class="mb-2" :class="device.hasBrandChoice ? 'col-md-6' : 'col-12'">
                                            <label for="video-ip" class="form-label">{{ $t('componentEditVideoDevice.ipAddress') }}</label>
                                            <input id="video-ip" type="text" class="form-control" :value="form.ip" @input="onIpInput"
                                                :placeholder="$t('componentEditVideoDevice.ipPlaceholder')" inputmode="decimal" autocomplete="off" required />
                                        </div>

                                        <!-- Marque : DAHUA ou HIK, un équipement ONVIF garde sa marque -->
                                        <div v-if="device.hasBrandChoice" class="mb-2 col-md-6">
                                            <label for="video-brand" class="form-label">{{ $t('componentEditVideoDevice.brand') }}</label>
                                            <select id="video-brand" class="form-select" v-model="device.brand">
                                                <option v-for="brand in selectableBrands" :key="brand" :value="brand">{{ brand }}</option>
                                            </select>
                                        </div>

                                        <div class="mb-2 col-md-6">
                                            <label for="video-username" class="form-label">{{ $t('componentEditVideoDevice.login') }}</label>
                                            <input id="video-username" type="text" class="form-control" v-model="form.username" maxlength="64"
                                                :placeholder="$t('componentEditVideoDevice.loginPlaceholder')" autocomplete="off" autocapitalize="off" spellcheck="false" required />
                                        </div>

                                        <!-- En édition, le mot de passe enregistré est conservé sauf demande de modification -->
                                        <div v-if="!isNew" class="mb-2 col-md-6 d-flex align-items-end">
                                            <div class="d-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2 w-100">
                                                <label class="form-check-label flex-grow-1 me-2" for="video-change-password">{{ $t('componentEditVideoDevice.changePassword') }}</label>
                                                <input id="video-change-password" type="checkbox" data-switch="bool" v-model="changePassword" @change="onChangePasswordToggle" />
                                                <label for="video-change-password" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                            </div>
                                        </div>

                                        <div v-if="isNew || changePassword" class="mb-2 col-md-6" :class="{ 'offset-md-6': !isNew }">
                                            <PasswordComponent id="video-password" v-model="form.password" :hint-text="$t('componentEditVideoDevice.passwordPlaceholder')"
                                                :maxlength="128" tabindex="0" />
                                        </div>
                                    </template>

                                    <template v-if="isCamera">
                                        <div class="mb-2 col-md-6">
                                            <div class="d-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                                <label class="form-check-label flex-grow-1 me-2" for="video-ptz">{{ $t('componentEditVideoDevice.ptz') }}</label>
                                                <input id="video-ptz" type="checkbox" data-switch="bool" v-model="device.ptz" />
                                                <label for="video-ptz" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                            </div>
                                        </div>
                                        <div class="mb-2 col-md-6">
                                            <div class="d-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                                <label class="form-check-label flex-grow-1 me-2" for="video-vod">{{ $t('componentEditVideoDevice.vod') }}</label>
                                                <input id="video-vod" type="checkbox" data-switch="bool" v-model="device.vod" />
                                                <label for="video-vod" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                            </div>
                                        </div>

                                        <!-- Fenêtre de vidéo à la demande attachée aux événements des lecteurs associés -->
                                        <template v-if="device.vod">
                                            <div class="mb-2 col-md-6">
                                                <label for="video-vod-pre-roll" class="form-label">{{ $t('componentEditVideoDevice.vodPreRoll') }}</label>
                                                <input id="video-vod-pre-roll" type="number" class="form-control" v-model.number="device.vod_pre_roll_secs" min="0" max="300" step="1" required />
                                            </div>
                                            <div class="mb-2 col-md-6">
                                                <label for="video-vod-duration" class="form-label">{{ $t('componentEditVideoDevice.vodDuration') }}</label>
                                                <input id="video-vod-duration" type="number" class="form-control" v-model.number="device.vod_duration_secs" min="1" max="300" step="1" required />
                                            </div>
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <!-- Lecteurs d'accès surveillés par la caméra -->
                        <div v-if="isCamera && device.vod" id="video-readers" class="card mb-3">
                            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                <h4 class="header-title">{{ $t('componentEditVideoDevice.readersSection') }}</h4>
                                <button id="video-add-readers" type="button" class="btn btn-sm btn-outline-primary" :disabled="availableReaders.length === 0" @click="addReaders">
                                    <i class="mdi mdi-plus me-1"></i>{{ $t('componentEditVideoDevice.readerAddTooltip') }}
                                </button>
                            </div>
                            <div class="card-body pt-2">
                                <p v-if="device.readers.length === 0" class="text-center mb-0">{{ $t('componentEditVideoDevice.readersEmpty') }}</p>
                                <table v-else class="table table-centered mb-0">
                                    <tbody>
                                        <tr v-for="reader in device.readers" :key="reader.key">
                                            <td>
                                                <span class="fw-bold text-primary">{{ readerLabel(reader) }}</span>
                                                <span v-if="readerHub(reader)" class="d-block font-13 fst-italic text-muted">{{ readerHub(reader) }}</span>
                                            </td>
                                            <td class="table-action text-end">
                                                <a href="javascript:void(0);" class="text-danger reader-delete" :title="$t('common.delete')" :aria-label="$t('common.delete')" @click="removeReader(reader)">
                                                    <i class="mdi mdi-trash-can font-18"></i>
                                                </a>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <!-- Paramètres avancés : valeurs par défaut de l'API quand ils sont vides -->
                        <div class="card mb-3">
                            <div class="card-header" :class="{ 'pb-0': advancedOpen }">
                                <a id="video-advanced-toggle" href="javascript:void(0);" class="d-flex align-items-center justify-content-between text-reset"
                                    :aria-expanded="advancedOpen" @click="advancedOpen = !advancedOpen">
                                    <h4 class="header-title mb-0">{{ $t('componentEditVideoDevice.advancedSection') }}</h4>
                                    <i :class="['mdi', 'font-20', advancedOpen ? 'mdi-chevron-up' : 'mdi-chevron-down']"></i>
                                </a>
                            </div>
                            <div v-show="advancedOpen" id="video-advanced" class="card-body">
                                <div class="row">
                                    <template v-if="hasOwnEndpoint || isCamera">
                                        <div class="mb-2 col-md-6">
                                            <label for="video-port" class="form-label">{{ $t('componentEditVideoDevice.rtspPort') }}</label>
                                            <input id="video-port" type="text" class="form-control" inputmode="numeric" maxlength="5" :value="form.port" @input="onDigitsInput('port', $event)"
                                                :placeholder="$t('componentEditVideoDevice.rtspPortHint')" autocomplete="off" />
                                        </div>
                                        <div class="mb-2 col-md-6">
                                            <label for="video-http-port" class="form-label">{{ $t('componentEditVideoDevice.httpPort') }}</label>
                                            <input id="video-http-port" type="text" class="form-control" inputmode="numeric" maxlength="5" :value="form.http_port" @input="onDigitsInput('http_port', $event)"
                                                :placeholder="$t('componentEditVideoDevice.httpPortHint')" autocomplete="off" />
                                        </div>
                                    </template>

                                    <template v-if="isCamera">
                                        <div class="mb-2 col-md-6">
                                            <label for="video-channel" class="form-label">{{ $t('componentEditVideoDevice.channel') }}</label>
                                            <input id="video-channel" type="text" class="form-control" inputmode="numeric" maxlength="3" :value="form.channel" @input="onDigitsInput('channel', $event)"
                                                :placeholder="$t('componentEditVideoDevice.channelHint')" autocomplete="off" />
                                        </div>
                                        <div class="mb-2 col-12">
                                            <label for="video-rtsp-path" class="form-label">{{ $t('componentEditVideoDevice.rtspPath') }}</label>
                                            <input id="video-rtsp-path" type="text" class="form-control" v-model="form.rtsp_path" maxlength="255" :required="needsRtspPath"
                                                :placeholder="$t('componentEditVideoDevice.rtspPathPlaceholder')" autocomplete="off" spellcheck="false" />
                                        </div>
                                        <div class="mb-2 col-12">
                                            <label for="video-rtsp-path-main" class="form-label">{{ $t('componentEditVideoDevice.rtspPathMain') }}</label>
                                            <input id="video-rtsp-path-main" type="text" class="form-control" v-model="form.rtsp_path_main" maxlength="255"
                                                :placeholder="$t('componentEditVideoDevice.rtspPathMainPlaceholder')" autocomplete="off" spellcheck="false" />
                                        </div>
                                        <div class="mb-2 col-12">
                                            <label for="video-ptz-url" class="form-label">{{ $t('componentEditVideoDevice.ptzUrl') }}</label>
                                            <input id="video-ptz-url" type="text" class="form-control" v-model="form.ptz_url" maxlength="255" inputmode="url"
                                                :placeholder="$t('componentEditVideoDevice.ptzUrlPlaceholder')" autocomplete="off" spellcheck="false" />
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="form-actions-sticky d-flex flex-wrap justify-content-center align-items-center gap-2">
                    <span v-if="saving && progress" id="video-progress" class="text-muted fst-italic me-2">
                        <span class="spinner-border spinner-border-sm me-1" role="status"></span>{{ progress }}
                    </span>
                    <router-link :to="pathToVideo" class="btn btn-outline-secondary">{{ $t('common.back') }}</router-link>
                    <button v-if="!isNew" id="video-delete" type="button" class="btn btn-danger" :disabled="saving" @click="confirmModal.show()">{{ $t('common.delete') }}</button>
                    <button type="submit" class="btn btn-primary" :disabled="saving">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>

        <OnvifDiscoveryModal ref="onvifModal" />
        <NvrChannelsModal ref="channelsModal" />
        <CameraReadersModal ref="readersModal" />
        <ConfirmationModal ref="confirmModal" :title="$t('common.confirmDelete')" :question="deleteQuestion" type="danger"
            :button1="$t('common.cancel')" :button2="$t('common.delete')" :action="deleteDevice" />
    </div>
</template>

<script>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';
import { NVR, Camera, CameraReader, VIDEO_BRANDS, BRAND_DAHUA, BRAND_HIK, BRAND_ONVIF } from '@/models/video';
import PasswordComponent from '@/components/password.vue';
import ConfirmationModal from '@/modals/confirmationModal.vue';
import OnvifDiscoveryModal from '@/modals/onvifDiscoveryModal.vue';
import NvrChannelsModal from '@/modals/nvrChannelsModal.vue';
import CameraReadersModal from '@/modals/cameraReadersModal.vue';
import { IPV4_REGEX, filterIpInput } from '@/utils/ipAddress';


// Trimmed before validation, as in the app (the password is kept as typed)
const TRIMMED_FIELDS = ['name', 'ip', 'username', 'rtsp_path', 'rtsp_path_main', 'channel', 'ptz_url', 'port', 'http_port'];

// Creation / edition of a video device (NVR or camera) of a LINK.
// Saving a new NVR first probes it through the LINK, then declares the NVR and the channels picked as cameras.
export default {
    name: 'ComponentEditVideoDevice',
    components: {
        PasswordComponent,
        ConfirmationModal,
        OnvifDiscoveryModal,
        NvrChannelsModal,
        CameraReadersModal,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const type = route.query.type === 'nvr' ? 'nvr' : 'camera';
        const deviceId = route.query.id != null ? parseInt(route.query.id) : null;
        const hub = userStore.getHub(facilityId, hubId);

        const formRef = ref(null);
        const onvifModal = ref(null);
        const channelsModal = ref(null);
        const readersModal = ref(null);
        const confirmModal = ref(null);
        const device = ref(null);
        const changePassword = ref(false);
        const advancedOpen = ref(false);
        const saving = ref(false);
        const progress = ref(null);
        const form = reactive({
            name: '', ip: '', username: '', password: '',
            port: '', http_port: '', channel: '', rtsp_path: '', rtsp_path_main: '', ptz_url: '',
        });

        const isNew = computed(() => device.value != null && device.value.isNew);
        const isCamera = computed(() => device.value != null && device.value.type === 'camera');
        // A camera attached to an NVR inherits its address and credentials
        const hasOwnEndpoint = computed(() => !(isCamera.value && device.value.isOnNvr));
        // ONVIF cameras have no deterministic RTSP path: it must be provided
        const needsRtspPath = computed(() => isCamera.value && device.value.isOnvif && hasOwnEndpoint.value);

        const pathToInstallationEditComponents = computed(() => `/installation-edit?id=${facilityId}&action=components`);
        const pathToVideo = computed(() => `/component-edit-video?facilityId=${facilityId}&hubId=${hubId}`);

        const deleteQuestion = computed(() => device.value ? device.value.deleteConfirm.replace(/\n/g, '<br>') : '');

        const initForm = () => {
            const d = device.value;
            form.name = d.name;
            form.ip = d.ip;
            form.username = d.username;
            form.password = d.password;
            form.port = d.port != null ? String(d.port) : '';
            form.http_port = d.http_port != null ? String(d.http_port) : '';
            if (isCamera.value) {
                form.channel = String(d.channel);
                form.rtsp_path = d.rtsp_path;
                form.rtsp_path_main = d.rtsp_path_main;
                form.ptz_url = d.ptz_url;
            }
            // Opened by default when the RTSP path is mandatory
            advancedOpen.value = needsRtspPath.value;
        };

        const findDevice = () => (type === 'nvr' ? hub.nvrs : hub.cameras).find(d => d.id === deviceId);

        onMounted(async () => {
            if (deviceId == null) {
                const brand = VIDEO_BRANDS.includes(route.query.brand) ? route.query.brand : BRAND_ONVIF;
                device.value = type === 'nvr' ? NVR.create(brand) : Camera.create(brand);
            } else {
                let found = findDevice();
                if (!found) {
                    // Opened without the video page: load the devices of the LINK
                    try {
                        await userStore.loadVideoDevices(facilityId, hub);
                        found = findDevice();
                    } catch (message) {
                        showToast('error', message);
                    }
                }
                if (!found) {
                    router.replace(pathToVideo.value);
                    return;
                }
                // Edit a copy: the list keeps its values until the update succeeds
                device.value = found.clone();
            }
            initForm();
        });

        // Digits only, as in the app
        const onDigitsInput = (field, event) => {
            const digits = event.target.value.replace(/\D/g, '');
            event.target.value = digits;
            event.target.setCustomValidity('');
            form[field] = digits;
        };

        // Digits and dots only, as in the app
        const onIpInput = (event) => {
            const value = filterIpInput(event.target.value);
            event.target.value = value;
            event.target.setCustomValidity('');
            form.ip = value;
        };

        const onChangePasswordToggle = () => {
            if (!changePassword.value) form.password = '';
        };

        const setValidity = (id, valid, message) => {
            const input = document.getElementById(id);
            if (input) input.setCustomValidity(valid ? '' : message);
        };

        const inRange = (value, min, max) => value === '' || (Number(value) >= min && Number(value) <= max);

        const validate = async () => {
            for (const field of TRIMMED_FIELDS) form[field] = form[field].trim();
            await nextTick();

            if (hasOwnEndpoint.value) {
                setValidity('video-ip', form.ip === '' || IPV4_REGEX.test(form.ip), t('componentEditVideoDevice.checkInput'));
                if (isNew.value || changePassword.value) {
                    setValidity('video-password', form.password.trim() !== '', t('componentEditVideoDevice.passwordPlaceholder'));
                }
            }
            if (hasOwnEndpoint.value || isCamera.value) {
                setValidity('video-port', inRange(form.port, 1, 65535), t('componentEditVideoDevice.invalidPort'));
                setValidity('video-http-port', inRange(form.http_port, 1, 65535), t('componentEditVideoDevice.invalidPort'));
            }
            if (isCamera.value) {
                setValidity('video-channel', inRange(form.channel, 1, 255), t('componentEditVideoDevice.invalidChannel'));
            }

            if (formRef.value.checkValidity()) return true;

            // Make an error of the collapsed advanced section visible, as in the app
            if (!advancedOpen.value) {
                advancedOpen.value = true;
                await nextTick();
            }
            formRef.value.reportValidity();
            return false;
        };

        const parseNumber = (text) => text === '' ? null : parseInt(text, 10);

        const applyForm = () => {
            const d = device.value;
            d.name = form.name;
            if (hasOwnEndpoint.value) {
                d.ip = form.ip;
                d.username = form.username;
                d.password = (isNew.value || changePassword.value) ? form.password : '';
            }
            if (hasOwnEndpoint.value || isCamera.value) {
                d.port = parseNumber(form.port);
                d.http_port = parseNumber(form.http_port);
            }
            if (isCamera.value) {
                d.channel = parseNumber(form.channel) ?? 1;
                d.rtsp_path = form.rtsp_path;
                d.rtsp_path_main = form.rtsp_path_main;
                d.ptz_url = form.ptz_url;
            }
        };

        // One POST per camera, the failures are counted
        const declareChannels = async (nvr, channels) => {
            let declared = 0;
            let failed = 0;
            for (let i = 0; i < channels.length; i++) {
                progress.value = t('video.declaringCameras', { current: i + 1, total: channels.length });
                const camera = Camera.fromProbedChannel(nvr, channels[i]);
                if (await userStore.declareCamera(facilityId, hub, camera)) {
                    declared++;
                } else {
                    failed++;
                }
            }
            if (failed > 0) {
                showToast('error', t('video.camerasDeclareFailed', { failed, declared }));
            }
        };

        const handleSubmit = async () => {
            if (saving.value || !(await validate())) return;

            applyForm();
            const d = device.value;
            const creating = d.isNew;
            saving.value = true;
            progress.value = null;

            try {
                // A new NVR is probed first: it must be a recorder, and the installer picks its cameras
                // before anything is written. Cancelling the selection creates nothing.
                let selectedChannels = null;
                if (d.type === 'nvr' && creating) {
                    progress.value = t('video.probing');
                    const probe = await userStore.probeVideoDevice(facilityId, hub, d);
                    if (!probe.is_nvr) {
                        showToast('error', t('video.notAnNvr'));
                        return;
                    }
                    // The probe tells the HTTP port it reached the device on
                    if (d.http_port == null && probe.http_port != null) {
                        d.http_port = probe.http_port;
                        form.http_port = String(probe.http_port);
                    }
                    progress.value = null;
                    selectedChannels = await channelsModal.value.show(probe.channels);
                    if (selectedChannels == null) return;
                }

                const id = await userStore.saveVideoDevice(facilityId, hub, d);

                if (selectedChannels && selectedChannels.length > 0) {
                    // Declare the selected channels as cameras of the new NVR
                    await declareChannels({ id: id ?? d.id, name: d.name, brand: d.brand }, selectedChannels);
                }

                await router.push(pathToVideo.value);
                showToast('success', creating ? d.addedMessage : t('common.updateSuccess'));
            } catch (message) {
                showToast('error', message);
            } finally {
                saving.value = false;
                progress.value = null;
            }
        };

        const discoverOnvifDevices = async () => {
            const result = await onvifModal.value.show(facilityId, hub);
            if (!result) return;

            form.ip = result.ip;
            setValidity('video-ip', true, '');
            // Prefill the label when the installer has not typed one yet
            if (isNew.value && (form.name === '' || form.name === device.value.name)) {
                form.name = result.label.substring(0, 32);
            }
        };

        // Every reader of the facility whose hub is known by the backend, any LINK
        const facilityReaders = computed(() => {
            const readers = [];
            for (const facilityHub of userStore.getFacilityHubs(facilityId)) {
                if (facilityHub.id == null) continue;
                for (const reader of facilityHub.readers || []) {
                    readers.push({
                        reader: new CameraReader(facilityHub.id, reader.position),
                        label: reader.label ? reader.label : t('video.readerPosition', { position: reader.position }),
                        hubLabel: facilityHub.label,
                    });
                }
            }
            return readers;
        });

        const findReader = (reader) => facilityReaders.value.find(item => item.reader.equals(reader));

        const availableReaders = computed(() => facilityReaders.value.filter(item => !device.value.readers.some(r => r.equals(item.reader))));

        const readerLabel = (reader) => {
            const item = findReader(reader);
            return item ? item.label : t('video.readerPosition', { position: reader.position });
        };

        const readerHub = (reader) => {
            const item = findReader(reader);
            return item ? item.hubLabel : null;
        };

        const addReaders = async () => {
            const selected = await readersModal.value.show(availableReaders.value);
            if (selected && selected.length > 0) {
                device.value.readers.push(...selected.map(item => item.reader));
            }
        };

        const removeReader = (reader) => {
            device.value.readers = device.value.readers.filter(r => !r.equals(reader));
        };

        const deleteDevice = async () => {
            try {
                await userStore.deleteVideoDevice(facilityId, hub, device.value);
                await router.push(pathToVideo.value);
            } catch (message) {
                showToast('error', message);
            }
        };

        return {
            formRef,
            onvifModal,
            channelsModal,
            readersModal,
            confirmModal,
            device,
            form,
            changePassword,
            advancedOpen,
            saving,
            progress,
            isNew,
            isCamera,
            hasOwnEndpoint,
            needsRtspPath,
            selectableBrands: [BRAND_DAHUA, BRAND_HIK],
            pathToInstallationEditComponents,
            pathToVideo,
            deleteQuestion,
            onDigitsInput,
            onIpInput,
            onChangePasswordToggle,
            handleSubmit,
            discoverOnvifDevices,
            availableReaders,
            readerLabel,
            readerHub,
            addReaders,
            removeReader,
            deleteDevice,
        };
    },
};
</script>
